"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function IntakeForm() {
  const {
    control,
    handleSubmit,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-border bg-card">
        <p className="font-mono text-[0.6875rem] tracking-[0.18em] uppercase mb-4 text-primary">
          § 06.1 — TRANSMITTED
        </p>
        <p className="font-default text-foreground mb-6 leading-relaxed">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-foreground text-foreground hover:bg-primary hover:text-primary-foreground">
          Send another request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.6875rem] tracking-[0.18em] uppercase text-muted-foreground">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Enter your name" className="h-12 border-border bg-background focus-visible:ring-1 focus-visible:ring-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.6875rem] tracking-[0.18em] uppercase text-muted-foreground">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="name@company.com" className="h-12 border-border bg-background" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="projectType" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.6875rem] tracking-[0.18em] uppercase text-muted-foreground">
              Service Required
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="h-12 w-full border-border bg-background">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="performance-marketing">Performance Marketing</SelectItem>
                  <SelectItem value="brand-transformation">Brand Transformation</SelectItem>
                  <SelectItem value="data-analytics">Data Analytics</SelectItem>
                  <SelectItem value="growth-strategy">Growth Strategy</SelectItem>
                  <SelectItem value="conversion-optimization">Conversion Optimization</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive">
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-13 py-3.5 bg-primary text-primary-foreground font-default font-bold group relative overflow-hidden hover:bg-primary/90">
        <span className="relative z-10 flex items-center justify-center gap-2">
          {isSubmitting ? "Processing..." : "Let's Talk Strategy"}
          <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
        </span>
      </Button>
    </Form>;
}
export default function SecureYourBlueprint() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="2112" id="secure-your-blueprint" className="relative overflow-hidden bg-background py-24 md:py-32 text-foreground">
      <div aria-hidden="true" className="pointer-events-none absolute -left-12 top-1/2 -translate-y-1/2 select-none font-sans leading-none hidden lg:block" style={{
      fontSize: "780px",
      WebkitTextStroke: "1px hsl(240 5% 15%)",
      color: "transparent",
      opacity: 0.06
    }}>
        06
      </div>

      <div className="relative mx-auto max-w-[1440px] px-5 md:px-8 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div ref={leftRef} className="lg:col-span-8 transition-all duration-700 ease-out" style={{
          opacity: leftVisible ? 1 : 0,
          transform: leftVisible ? "translateY(0)" : "translateY(16px)",
          transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
        }}>
            <div className="flex items-center gap-3 mb-4">
              <span className="font-mono text-[0.6875rem] tracking-[0.18em] uppercase font-medium text-primary">
                § 06
              </span>
              <span className="font-mono text-[0.6875rem] tracking-[0.18em] uppercase text-muted-foreground">
                — Catalyst Growth Lab / Strategy Intake
              </span>
            </div>

            <div className="flex items-center gap-2 mb-10">
              <div className="flex-1 h-px bg-border" />
              <div className="flex items-center gap-1">
                <span className="size-1 bg-primary" />
                <span className="size-1 bg-primary" />
                <span className="size-1 bg-primary" />
              </div>
            </div>

            <h2 className="font-default font-extrabold text-foreground" style={{
            fontSize: "clamp(2.5rem, 5.8vw, 5.5rem)",
            lineHeight: 0.98,
            letterSpacing: "-0.04em"
          }}>
              Ignite your{" "}
              <em className="font-default italic text-primary" style={{
              display: "inline-block",
              opacity: leftVisible ? 1 : 0,
              transform: leftVisible ? "translateX(0)" : "translateX(0.4em)",
              transition: "opacity 600ms ease-out 200ms, transform 600ms ease-out 200ms"
            }}>
                growth
              </em>{" "}
              engine.
            </h2>

            <div className="mt-10 max-w-md">
              <p className="font-default text-muted-foreground leading-relaxed" style={{
              fontSize: "1.125rem",
              lineHeight: 1.6
            }}>
                Ready to scale? Our team of performance architects is ready to audit your current funnel and deploy high-velocity growth strategies.
              </p>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-6 font-mono text-[0.6875rem] tracking-[0.18em] uppercase text-muted-foreground">
              <span>ROI-FOCUSED</span>
              <span>DATA-DRIVEN</span>
              <span className="text-foreground">LAB-024</span>
            </div>
          </div>

          <div ref={rightRef} className="lg:col-span-4 transition-all duration-700 ease-out" style={{
          opacity: rightVisible ? 1 : 0,
          transform: rightVisible ? "translateY(0)" : "translateY(16px)",
          transitionDelay: "120ms",
          transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
        }}>
            <div className="relative group">
              <div className="border border-border p-8 md:p-9 bg-card">
                <div className="flex items-center gap-3 mb-3">
                  <span className="font-mono text-[0.6875rem] tracking-[0.18em] uppercase font-medium text-primary">
                    § 06.1
                  </span>
                  <span className="font-mono text-[0.6875rem] tracking-[0.18em] uppercase text-muted-foreground">
                    — Intake
                  </span>
                </div>

                <div className="flex items-center gap-2 mb-7">
                  <div className="flex-1 h-px bg-border" />
                  <div className="flex items-center gap-1">
                    <span className="size-1 bg-primary" />
                    <span className="size-1 bg-primary" />
                    <span className="size-1 bg-primary" />
                  </div>
                </div>

                <h3 className="font-default font-bold text-foreground mb-6" style={{
                fontSize: "1.5rem",
                lineHeight: 1.15,
                letterSpacing: "-0.02em"
              }}>
                  Start the transformation.
                </h3>

                <FormProvider formKey="secure_blueprint_form_home" formId="secure_blueprint_form_home" sectionName="secure-your-blueprint" defaultValues={{
                name: "",
                email: "",
                projectType: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                projectType: {
                  required: true
                }
              }} submitText="Let's Talk Strategy" successMessage="Inquiry received. Our growth architects will review your data and contact you within 24 hours." errorMessage="Submission failed. Please contact us directly at hello@catalystgrowth.com">
                  <IntakeForm />
                </FormProvider>

                <p className="mt-6 font-mono text-[0.6875rem] tracking-[0.04em] text-muted-foreground leading-relaxed">
                  Direct contact: hello@catalystgrowth.com
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}